/* eslint-disable prefer-const */
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import {updateObject} from 'utils/reducerUtils';
import {isTablet, osName} from 'react-device-detect';
import AudioPlayer from 'components/AudioPlayer';

import audioFilesNo from './languages/no-audioFiles-norming';
import audioFilesNn from './languages/nn-audioFiles-norming';
import assignments from './languages/data.json';
import strings from 'utils/strings';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faVolumeHigh} from '@fortawesome/free-solid-svg-icons';

const audioFilesObj = {no: audioFilesNo, nn: audioFilesNn};
const maxAudioPlayCount = 2;

function closestFallback(el, selector) {
	while (el) {
		if (
			(el.matches && el.matches(selector)) ||
			(el.msMatchesSelector && el.msMatchesSelector(selector)) ||
			(el.webkitMatchesSelector && el.webkitMatchesSelector(selector))
		)
			return el;
		el = el.parentElement;
	}
	return null;
}
function findNextInput(current) {
	var row = closestFallback(current, '.lit-revealed');
	var nextRow = row && row.nextElementSibling ? row.nextElementSibling : null;
	return nextRow
		? nextRow.querySelector('.lit-input__field:not(:disabled)')
		: null;
}
function focusIf(el) {
	if (el && typeof el.focus === 'function') el.focus();
}
function smoothScrollIntoView(el) {
	if (!el) return;
	try {
		el.scrollIntoView({behavior: 'smooth', block: 'center'});
	} catch (e) {
		el.scrollIntoView();
	}
}

class Norming extends React.Component {
	constructor(props) {
		super(props);

		this.audioFiles = audioFilesObj[strings.getLanguage()];
		this.audioRefs = this.props.refs || {};

		const results = {};
		for (const assignment of assignments)
			results[assignment.id] = assignment.value;

		this.state = {
			results,
			disabledAssignments: {},
			audioPlayCounts: {},
			// tablet-only UI state:
			showWordAudio: false,
			currentClickedIndex: null,
			showQuestionPlayButton: true,
			isChromBook: osName === 'Chromium OS',
		};
	}

	setResultsOnChange(id, value) {
		const {results} = this.state;

		this.setState({
			results: updateObject(results, {
				[id]: value,
			}),
		});
	}

	setResults(id, value) {
		this.setState((prev) => {
			const results = updateObject(prev.results, {[id]: value});
			const disabledAssignments = {};
			for (let i = 0; i < assignments.length; i++) {
				const aId = assignments[i].id;
				disabledAssignments[aId] = !!(
					results[aId] && String(results[aId]).trim() !== ''
				);
			}
			return {results, disabledAssignments};
		});
	}

	getDisabledAssignmentsFromStateResults() {
		const {results} = this.state;
		const disabledAssignments = {};
		for (let i = 0; i < assignments.length; i++) {
			const assignmentId = assignments[i].id;
			if (results[assignmentId]) disabledAssignments[assignmentId] = true;
		}
		return disabledAssignments;
	}

	renderRowDesktop(assignment, audioPlayCounts) {
		const assignmentId = assignment.id;
		const audioFile = this.audioFiles[assignmentId];
		const assignmentAudioPlayCount = audioPlayCounts[assignmentId] || 0;
		const isDisabled = !!this.state.disabledAssignments[assignmentId];
		const isAudioDisabled =
			isDisabled || assignmentAudioPlayCount === maxAudioPlayCount;

		let audioPlayerRef;

		return (
			<div
				className={classNames('lit-input lit-revealed', {
					'lit-input--disabled': isDisabled,
				})}
				key={assignmentId}
			>
				<AudioPlayer
					strokeColor="#f9fcfcff"
					outlineColor="#404040ff"
					disabled={isAudioDisabled}
					preload="auto"
					id={assignmentId}
					onStart={() => {
						this.setState({
							audioPlayCounts: updateObject(audioPlayCounts, {
								[assignmentId]: assignmentAudioPlayCount + 1,
							}),
						});
					}}
					onErrorPlay={(err) => {
						this.setState({
							audioPlayCounts: updateObject(audioPlayCounts, {
								[assignmentId]: Math.max(
									0,
									assignmentAudioPlayCount - 1
								),
							}),
						});
						console.log('Audio play error', err);
					}}
					ref={(ref) => {
						audioPlayerRef = ref;
					}}
					refs={this.audioRefs}
					src={audioFile}
				/>

				<input
					className="lit-input__field"
					disabled={isDisabled}
					placeholder={strings.writeWordHere}
					spellCheck={false}
					autoComplete="off"
					autoCorrect="off"
					autoCapitalize="off"
					inputMode="text"
					aria-autocomplete="none"
					enterKeyHint="next"
					data-gramm="false"
					data-enable-grammarly="false"
					name={`norming-${assignmentId}`}
					type="text"
					onChange={(ev) => {
						if (isDisabled) {
							return;
						}
						const value = ev.target.value;
						if (value.trim() !== '')
							this.setResultsOnChange(assignmentId, value);
					}}
					onFocus={() => {
						const da =
							this.getDisabledAssignmentsFromStateResults();
						da[assignmentId] = false;

						if (assignmentAudioPlayCount === 0 && audioPlayerRef) {
							audioPlayerRef.handleStartPlayer();
						}
						this.setState({disabledAssignments: da});
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							e.currentTarget.blur();

							var next = findNextInput(e.currentTarget);
							if (next) {
								focusIf(next);
								smoothScrollIntoView(next);
							} else {
								var wrapper = closestFallback(
									e.currentTarget,
									'.lit-norming'
								);
								var doneBtn = wrapper
									? wrapper.querySelector(
											'.lit-btn.bg-primary'
									  )
									: null;
								focusIf(doneBtn);
							}
						}
					}}
				/>
			</div>
		);
	}

	renderRowTablet(assignment, index, audioPlayCounts) {
		const assignmentId = assignment.id;
		const mainIndex = index + 1;
		const audioFile = this.audioFiles[assignmentId];
		const assignmentAudioPlayCount = audioPlayCounts[assignmentId] || 0;
		const isDisabled = !!this.state.disabledAssignments[assignmentId];
		const isAudioDisabled =
			isDisabled || assignmentAudioPlayCount === maxAudioPlayCount;

		const {showWordAudio, currentClickedIndex, showQuestionPlayButton} =
			this.state;

		let audioPlayerRef;

		return (
			<div
				style={{display: 'flex', marginTop: '50px'}}
				className={classNames('lit-input lit-revealed', {
					'lit-input--disabled': isDisabled,
				})}
				key={assignmentId}
			>
				<div
					style={{height: '50px', width: '50px', marginRight: '20px'}}
				>
					{showQuestionPlayButton && (
						<FontAwesomeIcon
							className={
								!isAudioDisabled
									? 'lit-play-button-ordiktat'
									: 'lit-play-button-ordiktat-disabled'
							}
							size="lg"
							icon={faVolumeHigh}
							onClick={() => {
								if (!isAudioDisabled) {
									this.setState({
										showWordAudio: true,
										showQuestionPlayButton: false,
										currentClickedIndex: mainIndex,
									});
								}
							}}
						/>
					)}
					{!showQuestionPlayButton &&
						currentClickedIndex !== mainIndex && (
							<FontAwesomeIcon
								size="lg"
								className="lit-play-button-ordiktat-disabled"
								icon={faVolumeHigh}
							/>
						)}
					{showWordAudio && currentClickedIndex === mainIndex && (
						<div>
							<AudioPlayer
								disabled={isAudioDisabled}
								id={assignmentId}
								strokeColor="#eeee08ff"
								outlineColor="#414343ff"
								preload="auto"
								autoplay
								onStart={() => {
									this.setState({
										audioPlayCounts: updateObject(
											audioPlayCounts,
											{
												[assignmentId]:
													assignmentAudioPlayCount +
													1,
											}
										),
									});
								}}
								onComplete={() => {
									this.setState({
										showQuestionPlayButton: true,
										currentClickedIndex: null,
									});
								}}
								onErrorPlay={(err) => {
									// if play fails, restore the button and fix counter
									this.setState({
										showQuestionPlayButton: true,
										currentClickedIndex: null,
										audioPlayCounts: updateObject(
											audioPlayCounts,
											{
												[assignmentId]: Math.max(
													0,
													assignmentAudioPlayCount - 1
												),
											}
										),
									});
								}}
								ref={(ref) => {
									audioPlayerRef = ref;
								}}
								refs={this.audioRefs}
								src={audioFile}
							/>
						</div>
					)}
				</div>

				<input
					className="lit-input__field"
					disabled={isDisabled}
					placeholder={strings.writeWordHere}
					spellCheck={false}
					autoComplete="off"
					autoCorrect="off"
					autoCapitalize="off"
					inputMode="text"
					aria-autocomplete="none"
					enterKeyHint="next"
					data-gramm="false"
					data-enable-grammarly="false"
					name={`norming-${assignmentId}`}
					type="text"
					onChange={(ev) => {
						if (isDisabled) {
							return;
						}
						const value = ev.target.value;
						if (value.trim() !== '')
							this.setResultsOnChange(assignmentId, value);
					}}
					onFocus={() => {
						const da =
							this.getDisabledAssignmentsFromStateResults();
						da[assignmentId] = false;

						if (!isAudioDisabled) {
							this.setState({
								showWordAudio: true,
								showQuestionPlayButton: false,
								currentClickedIndex: mainIndex,
							});
						}
						this.setState({disabledAssignments: da});
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							e.currentTarget.blur();

							var next = findNextInput(e.currentTarget);
							if (next) {
								focusIf(next);
								smoothScrollIntoView(next);
							} else {
								var wrapper = closestFallback(
									e.currentTarget,
									'.lit-norming'
								);
								var doneBtn = wrapper
									? wrapper.querySelector(
											'.lit-btn.bg-primary'
									  )
									: null;
								focusIf(doneBtn);
							}
						}
					}}
				/>
			</div>
		);
	}

	render() {
		const {onNextStep} = this.props;
		const {audioPlayCounts, results, isChromBook} = this.state;

		const allResultsHasValues = Object.keys(results).every(
			(key) => results[key]
		);
		const isNextButtonVisible = allResultsHasValues;

		return (
			<div className="lit-wrapper--thin lit-test lit-diktat lit-norming">
				{(!isTablet || !isChromBook) &&
					assignments.map((assignment) =>
						this.renderRowDesktop(assignment, audioPlayCounts)
					)}

				{(isTablet || isChromBook) &&
					assignments.map((assignment, index) =>
						this.renderRowTablet(assignment, index, audioPlayCounts)
					)}

				{isNextButtonVisible && (
					<button
						className="lit-btn lit bg-primary"
						onClick={() => onNextStep(results)}
					>
						{strings.done}
					</button>
				)}
			</div>
		);
	}
}

const mapStateToProps = () => ({});
export default connect(mapStateToProps)(Norming);
